import SSRIcon from '@ingka/ssr-icon'
import warningTriangle from '@ingka/ssr-icon/paths/warning-triangle'
import Text from '@ingka/text'
import { space25, space100 } from '@ingka/variables'
import { Flex } from '@mantine/core'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Conversion, Focus, SkillData } from 'lib/types/coworkerData.types'

import { colourBrandYellow, colourSemanticCaution } from 'styles/tokens/oldSkapa.tokens'

import ConversionInfoBody from './components/ConversionInfoBody.component'
import EmptyPanel from './components/EmptyPanel.component'
import MainMetric from './components/MainMetric.component'
import ConversionGraph from 'components/composites/Graphs/conversionGraph.component'
import InfoPanel from 'components/composites/Shared/InfoPanel/InfoPanel.component'

import { useSuggestedFocus } from '../useSuggestedFocus.hook'
import SubPanelLayout from './SubPanel.layout'

interface Props {
  data: SkillData
}

const ConversionSubPanel = ({ data }: Props) => {
  if (!data.conversion) {
    return (
      <SubPanelLayout
        leftPanel={<EmptyPanel title={'Conversion'} />}
        rightPanel={<EmptyPanel title={'Conversion'} />}
      />
    )
  }

  return (
    <SubPanelLayout
      leftPanel={<ConversionLeftPanel data={data} />}
      rightPanel={<ConversionRightPanel data={data.conversion} />}
    />
  )
}

const ConversionLeftPanel = ({ data }: { data: SkillData }) => {
  const [showModal, setShowModal] = useState(false)
  const { t } = useTranslation()
  const suggestedFocusOpacity = useSuggestedFocus(Focus.Conversion, data)
  const showTopPart =
    !data.conversion || !data.conversion.conversionR30.enoughVolume || suggestedFocusOpacity === 1

  return (
    <Flex direction={'column'} gap={space100} w={'100%'} justify={'space-evenly'}>
      {showTopPart && (
        <Flex justify={'space-between'}>
          <Text
            headingSize="s"
            style={{
              backgroundColor: colourBrandYellow,
              padding: space25,
              borderRadius: '5px',
              whiteSpace: 'nowrap',
              opacity: suggestedFocusOpacity,
            }}
          >
            {t('features.1-efficiency.highest-potential')}
          </Text>
          {!data?.conversion?.conversionR30.enoughVolume && (
            <Text bodySize="s" style={{ textAlign: 'center' }}>
              <SSRIcon
                paths={warningTriangle}
                style={{ marginRight: space25 }}
                color={colourSemanticCaution}
              />
              {t('features.1-efficiency.low-data-volume')}
            </Text>
          )}
        </Flex>
      )}

      <Flex justify={'space-between'}>
        <Flex direction={'column'}>
          <Text headingSize="m">
            {t('features.1-efficiency.your-now', { metric: 'Conversion Rate' })}
          </Text>
          <Text bodySize="s">{t('features.1-efficiency.your-now-subtitle')}</Text>
        </Flex>
        <InfoPanel
          body={<ConversionInfoBody />}
          showModal={showModal}
          handleClose={() => setShowModal(false)}
          handleOpen={() => setShowModal(true)}
        />
      </Flex>

      <Flex style={{ opacity: data?.conversion?.conversionR30.enoughVolume ? 1 : 0.5 }}>
        <MainMetric
          value={data?.conversion?.conversionR30.conversionRate ?? null}
          avg={data?.conversion?.conversionR30.avgConversionRate?.toFixed(1) ?? 'N/A'}
          top={data?.conversion?.conversionR30.maxConversionRate ?? null}
          unit="%"
          color="#95E5BB"
          topPerformerText={'features.1-efficiency.country-highest'}
        />
      </Flex>
    </Flex>
  )
}

const ConversionRightPanel = ({ data }: { data: Conversion }) => {
  const { t } = useTranslation()

  return (
    <Flex direction={'column'}>
      {data.conversionTrendWeek.length > 0 ? (
        <>
          <Text headingSize="m">
            {t('features.1-efficiency.your-trend', { metric: 'Conversion Rate' })}
          </Text>
          <Text bodySize="s">
            {t('features.1-efficiency.your-trend-subtitle', { metric: 'Conversion Rate' })}
          </Text>

          <Flex w={'100%'} h={300} style={{ marginTop: space100 }}>
            <ConversionGraph data={data} />
          </Flex>
        </>
      ) : (
        <Text bodySize="s" style={{ textAlign: 'center' }}>
          <SSRIcon paths={warningTriangle} style={{ marginRight: space25 }} />
          {t('No trend data available')}
        </Text>
      )}
    </Flex>
  )
}

export default ConversionSubPanel
